import { useCallback, useState } from "react";
import config from "../../../config";
import usePublicChatStore from "../usePublicChatStore";
import {
  buildExternalAccessPolicy,
  parseOptionalPositiveInt,
  periodUnitToSeconds,
} from "./externalAccess";

export function usePublicShareLinkActions({
  agentId,
  externalAccessPolicy,
  externalChatMaxSessionsPerIdentity,
  externalChatMaxSessionsTotal,
  externalChatRateLimitMessages,
  externalChatRateLimitPeriodValue,
  externalChatRateLimitPeriodUnit,
  externalChatRateLimitExceededMessage,
  enablePublicShareLinkExpiry,
  publicShareLinkExpiresAt,
}) {
  const [publicShareLink, setPublicShareLink] = useState(null);
  const [publicShareLinkError, setPublicShareLinkError] = useState("");
  const [creatingPublicShareLink, setCreatingPublicShareLink] = useState(false);
  const [revokingPublicShareLink, setRevokingPublicShareLink] = useState(false);

  const loadLatestPublicShareLink = useCallback(
    async ({ ignore } = {}) => {
      if (!agentId) {
        setPublicShareLink(null);
        setPublicShareLinkError("");
        return;
      }
      const latestShareLink = await usePublicChatStore
        .getState()
        .getLatestPublicChatShareLink(agentId);
      if (ignore?.()) return;
      if (latestShareLink?.shareId) {
        setPublicShareLink(latestShareLink);
      } else {
        setPublicShareLink(null);
      }
    },
    [agentId]
  );

  const resetPublicShareLink = useCallback(() => {
    setPublicShareLink(null);
    setPublicShareLinkError("");
    setCreatingPublicShareLink(false);
    setRevokingPublicShareLink(false);
  }, []);

  const handleCreatePublicShareLink = useCallback(async () => {
    if (!agentId || creatingPublicShareLink || revokingPublicShareLink) return;
    setCreatingPublicShareLink(true);
    setPublicShareLinkError("");
    try {
      const normalizedExternalAccessPolicy =
        buildExternalAccessPolicy(externalAccessPolicy);
      const payload = {
        accessPolicy: normalizedExternalAccessPolicy,
        accessMode: normalizedExternalAccessPolicy.mode,
        allowedEmails: normalizedExternalAccessPolicy.allowedEmails,
        maxSessionsPerIdentity: parseOptionalPositiveInt(
          externalChatMaxSessionsPerIdentity
        ),
        maxSessionsTotal: parseOptionalPositiveInt(
          externalChatMaxSessionsTotal
        ),
        rateLimitMessages: parseOptionalPositiveInt(
          externalChatRateLimitMessages
        ),
        rateLimitPeriodSeconds: (() => {
          const periodValue = parseOptionalPositiveInt(
            externalChatRateLimitPeriodValue
          );
          return periodValue
            ? periodValue * periodUnitToSeconds(externalChatRateLimitPeriodUnit)
            : null;
        })(),
        rateLimitExceededMessage:
          externalChatRateLimitExceededMessage.trim() || null,
      };
      if (enablePublicShareLinkExpiry && publicShareLinkExpiresAt) {
        const expiresDate = new Date(publicShareLinkExpiresAt);
        if (!Number.isNaN(expiresDate.getTime())) {
          payload.expiresAt = expiresDate.toISOString();
        }
      }
      const created = await usePublicChatStore
        .getState()
        .createPublicChatShareLink(agentId, payload);
      if (created?.url) {
        setPublicShareLink(created);
      } else {
        setPublicShareLinkError(
          usePublicChatStore.getState().error ||
            "Failed to create public chat share link."
        );
      }
    } catch (error) {
      setPublicShareLinkError(
        error?.message || "Failed to create public chat share link."
      );
    } finally {
      setCreatingPublicShareLink(false);
    }
  }, [
    agentId,
    creatingPublicShareLink,
    revokingPublicShareLink,
    externalAccessPolicy,
    externalChatMaxSessionsPerIdentity,
    externalChatMaxSessionsTotal,
    externalChatRateLimitMessages,
    externalChatRateLimitPeriodValue,
    externalChatRateLimitPeriodUnit,
    externalChatRateLimitExceededMessage,
    enablePublicShareLinkExpiry,
    publicShareLinkExpiresAt,
  ]);

  const handleRevokePublicShareLink = useCallback(async () => {
    if (!agentId || !publicShareLink?.shareId || revokingPublicShareLink)
      return;
    setRevokingPublicShareLink(true);
    setPublicShareLinkError("");
    try {
      const revoked = await usePublicChatStore
        .getState()
        .revokePublicChatShareLink(agentId, publicShareLink.shareId, {});
      if (revoked) {
        setPublicShareLink(null);
      } else {
        setPublicShareLinkError(
          usePublicChatStore.getState().error ||
            "Failed to revoke public chat share link."
        );
      }
    } catch (error) {
      setPublicShareLinkError(
        error?.message || "Failed to revoke public chat share link."
      );
    } finally {
      setRevokingPublicShareLink(false);
    }
  }, [agentId, publicShareLink, revokingPublicShareLink]);

  const handleCopyPublicShareLink = useCallback(() => {
    const url =
      publicShareLink?.url ||
      (publicShareLink?.tokenValue
        ? config.buildAgentChatUrl(publicShareLink.tokenValue)
        : "");
    if (!url) return;
    navigator.clipboard.writeText(url);
  }, [publicShareLink]);

  return {
    publicShareLink,
    setPublicShareLink,
    publicShareLinkError,
    setPublicShareLinkError,
    creatingPublicShareLink,
    setCreatingPublicShareLink,
    revokingPublicShareLink,
    setRevokingPublicShareLink,
    loadLatestPublicShareLink,
    resetPublicShareLink,
    handleCreatePublicShareLink,
    handleRevokePublicShareLink,
    handleCopyPublicShareLink,
  };
}
